import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TreePine, Calculator } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CalculatorTransparency, transparencyData } from './CalculatorTransparency';

const VirkesvolymKalkylator = () => {
  const { language } = useLanguage();
  const [tradslag, setTradslag] = useState('gran');
  const [diameter, setDiameter] = useState(25);
  const [hojd, setHojd] = useState(20);
  const [antalTrad, setAntalTrad] = useState(100);
  const [result, setResult] = useState(null);

  // Form factors by species
  const formFactors = {
    gran: 0.42,
    tall: 0.40,
    bjork: 0.38,
    ek: 0.45,
    bok: 0.44
  };

  // Swedish volume formula (simplified Brandel's formula)
  const calculateVolume = () => {
    const formFactor = formFactors[tradslag] || 0.42;
    const radiusM = diameter / 200; // cm to m, then radius
    const basalArea = Math.PI * radiusM * radiusM;
    const volumePerTree = basalArea * hojd * formFactor;
    const totalVolume = volumePerTree * antalTrad;
    
    setResult({
      volumePerTree: volumePerTree.toFixed(3),
      totalVolume: totalVolume.toFixed(2),
      formFactor: formFactor
    });
  };

  const speciesNames = {
    gran: 'Gran',
    tall: 'Tall',
    bjork: 'Björk',
    ek: 'Ek',
    bok: 'Bok'
  };

  const content = {
    sv: {
      title: 'Virkesvolym kalkylator',
      description: 'Beräkna virkesvolym baserat på trädslag, diameter, höjd och antal träd. Verktyget använder svenska skogliga formler för att uppskatta kubikinnehållet i din skog.',
      treeSpecies: 'Trädslag',
      diameter: 'Diameter (DBH) cm',
      height: 'Höjd (m)',
      treeCount: 'Antal träd',
      calculate: 'Beräkna volym',
      result: 'Resultat',
      volumePerTree: 'Volym per träd',
      totalVolume: 'Total volym'
    },
    de: {
      title: 'Holzvolumen Rechner',
      description: 'Berechnen Sie das Holzvolumen basierend auf Baumart, Durchmesser, Höhe und Anzahl der Bäume.',
      treeSpecies: 'Baumart',
      diameter: 'Durchmesser (BHD) cm',
      height: 'Höhe (m)',
      treeCount: 'Anzahl Bäume',
      calculate: 'Volumen berechnen',
      result: 'Ergebnis',
      volumePerTree: 'Volumen pro Baum',
      totalVolume: 'Gesamtvolumen'
    },
    en: {
      title: 'Timber Volume Calculator',
      description: 'Calculate timber volume based on tree species, diameter, height, and tree count using Swedish forestry formulas.',
      treeSpecies: 'Tree species',
      diameter: 'Diameter (DBH) cm',
      height: 'Height (m)',
      treeCount: 'Number of trees',
      calculate: 'Calculate volume',
      result: 'Result',
      volumePerTree: 'Volume per tree',
      totalVolume: 'Total volume'
    },
    se: {
      title: 'Muorravoluma rehkenastin',
      description: 'Rehkenas muorravoluma.',
      treeSpecies: 'Muorralágat',
      diameter: 'Diametera (cm)',
      height: 'Allodahka (m)',
      treeCount: 'Muoraid lohku',
      calculate: 'Rehkenas voluma',
      result: 'Boađus'
    }
  };

  const c = content[language] || content.sv;
  const transparency = transparencyData.virkesvolym?.[language] || transparencyData.virkesvolym?.sv;

  // Build assumptions from current values
  const assumptions = [
    { label: 'Trädslag', value: speciesNames[tradslag], unit: '' },
    { label: 'Formfaktor', value: formFactors[tradslag], unit: '' },
    { label: 'Diameter', value: diameter, unit: 'cm' },
    { label: 'Höjd', value: hojd, unit: 'm' }
  ];

  return (
    <Card className="w-full" data-testid="virkesvolym-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <TreePine className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="tradslag">{c.treeSpecies}</Label>
            <Select value={tradslag} onValueChange={setTradslag}>
              <SelectTrigger id="tradslag" data-testid="tradslag-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gran">Gran</SelectItem>
                <SelectItem value="tall">Tall</SelectItem>
                <SelectItem value="bjork">Björk</SelectItem>
                <SelectItem value="ek">Ek</SelectItem>
                <SelectItem value="bok">Bok</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="diameter">{c.diameter}</Label>
            <Input
              id="diameter"
              type="number"
              value={diameter}
              onChange={(e) => setDiameter(Number(e.target.value))}
              min={5}
              max={100}
              data-testid="diameter-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="hojd">{c.height}</Label>
            <Input
              id="hojd"
              type="number"
              value={hojd}
              onChange={(e) => setHojd(Number(e.target.value))}
              min={1}
              max={40}
              data-testid="hojd-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="antal">{c.treeCount}</Label>
            <Input
              id="antal"
              type="number"
              value={antalTrad}
              onChange={(e) => setAntalTrad(Number(e.target.value))}
              min={1}
              data-testid="antal-input"
            />
          </div>
        </div>

        <Button 
          onClick={calculateVolume} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-2">{c.result}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{c.volumePerTree}</p>
                <p className="text-xl font-bold text-forest-700">{result.volumePerTree} m³</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.totalVolume} ({antalTrad} {speciesNames[tradslag]})</p>
                <p className="text-2xl font-bold text-forest-700">{result.totalVolume} m³</p>
              </div>
            </div>
          </div>
        )}

        {/* Transparency Section */}
        {result && transparency && (
          <CalculatorTransparency
            assumptions={assumptions}
            howItWorks={transparency.howItWorks}
            sources={transparency.sources}
            didYouKnow={transparency.didYouKnow}
            relatedCalculators={transparency.relatedCalculators}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default VirkesvolymKalkylator;
